import { memo, useMemo } from "react";
import { STAT_TYPES } from "../../constants";
import { StatTable } from "../stat-table/stat-table";
import SprayChart from "../spray-chart/spray-chart";
import { RollingChart } from "../rolling-chart/rolling-chart";
import { PositionChart } from "../position-chart/position-chart";
import { SimilarBatters } from "../similar-players/similar-batters";
import { SimilarPitchers } from "../similar-players/similar-pitchers";
import { PitcherGameLog } from "../pitcher-game-log/pitcher-game-log";

const getYear = (s) => s?.year ?? null;

const BATTER_TABS = new Set([
  STAT_TYPES.BATTING,
  STAT_TYPES.BASERUNNING,
  STAT_TYPES.SITUATIONAL,
  STAT_TYPES.SPLITS,
  STAT_TYPES.BATTED_BALL,
  STAT_TYPES.VALUE,
]);

const SPRAY_CHART_TABS = new Set([
  STAT_TYPES.BATTING,
  STAT_TYPES.BASERUNNING,
  STAT_TYPES.SITUATIONAL,
  STAT_TYPES.SPLITS,
  STAT_TYPES.BATTED_BALL,
]);

export const PlayerContent = memo(
  ({ activeTab, statCategories, selectedDivision, playerId, playerData, selectedYear }) => {
    const stats = useMemo(() => statCategories[activeTab]?.stats ?? [], [statCategories, activeTab]);
    const statType = statCategories[activeTab]?.type || activeTab;

    const playerType = useMemo(
      () => (BATTER_TABS.has(activeTab) ? "batter" : "pitcher"),
      [activeTab]
    );

    const currentYear = useMemo(() => {
      if (selectedYear) return selectedYear;
      if (!stats?.length) return new Date().getFullYear();
      return Math.max(...stats.map((s) => getYear(s) ?? 0));
    }, [selectedYear, stats]);

    const shouldShowSprayChart = SPRAY_CHART_TABS.has(activeTab) && stats.length > 0;
    const shouldShowSimilarBatters = shouldShowSprayChart;
    const shouldShowSimilarPitchers = playerType === "pitcher" && stats.length > 0;

    return (
      <div className="p-6 space-y-8">
        {stats.length > 0 && <StatTable stats={stats} type={statType} />}

        {shouldShowSimilarPitchers && (
          <SimilarPitchers playerId={playerId} year={currentYear} division={selectedDivision} />
        )}

        {playerType === "pitcher" && stats.length > 0 && (
          <RollingChart playerId={playerId} playerType={playerType} initialWindow={25} />
        )}

        {shouldShowSimilarPitchers && (
          <PitcherGameLog playerId={playerId} division={selectedDivision} />
        )}

        {shouldShowSimilarBatters && (
          <SimilarBatters playerId={playerId} year={currentYear} division={selectedDivision} />
        )}

        {shouldShowSprayChart && (
          <SprayChart playerId={playerId} year={currentYear} division={selectedDivision} />
        )}

        {playerType === "batter" && shouldShowSprayChart && (
          <PositionChart playerId={playerId} year={currentYear} division={selectedDivision} />
        )}

        {playerType === "batter" && shouldShowSprayChart && (
          <RollingChart playerId={playerId} playerType={playerType} initialWindow={25} />
        )}
      </div>
    );
  }
);

PlayerContent.displayName = "PlayerContent";
